import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Typography,
  CircularProgress,
  IconButton,
  Tabs,
  Tab,
  Box,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CollectionsIcon from "@mui/icons-material/Collections";
import { styled } from "@mui/system";
import { useGlobalStyles } from "../../../../theme/global";
import { StyledInputLabel } from "../../../../theme/StyledInputLabel";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AssetLibraryModal from "../../../AssetLibraryModal";
import { Asset, AvatarGroupItem, CustomPhotoAvatar } from "../../../../context/types";
import { THEME_COLORS } from "../../../../context/constants";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    padding: "10px",
    minWidth: "500px",
  },
}));

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

interface AvatarCreationModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (
    data: any,
    type: "generate" | "upload" | "library" | "video",
    imageFile?: File,
    assetUrl?: string,
    videoFile?: File
  ) => Promise<void>;
  isLoading: boolean;
  // V3: lazy-load existing identities (avatar groups) for the "attach to existing identity" picker
  loadExistingIdentities?: () => Promise<AvatarGroupItem[]>;
  // V3: rename a look (hidden for prompt-type avatars)
  onRenameLook?: (avatar: CustomPhotoAvatar, newName: string) => Promise<void>;
}

const AvatarCreationModal: React.FC<AvatarCreationModalProps> = ({
  open,
  onClose,
  onSubmit,
  isLoading,
  loadExistingIdentities,
}) => {
  const globalClasses = useGlobalStyles();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedVideoFile, setSelectedVideoFile] = useState<File | null>(null);
  const [assetLibraryOpen, setAssetLibraryOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  // V3: existing identities picker
  const [existingIdentities, setExistingIdentities] = useState<AvatarGroupItem[]>([]);
  const [identitiesLoaded, setIdentitiesLoaded] = useState(false);
  const [selectedAvatarGroupId, setSelectedAvatarGroupId] = useState<string>("");
  // V3: reference images (only allowed when avatarGroupId is set)
  const [referenceImageUrls, setReferenceImageUrls] = useState<string[]>([]);

  const [formData, setFormData] = useState({
    name: "",
    age: "Unspecified" as const,
    gender: "Unspecified" as const,
    ethnicity: "Unspecified",
    orientation: "square" as const,
    pose: "half_body" as const,
    style: "Unspecified" as const,
    appearance: "",
  });

  // Lazy-load identities when modal opens
  React.useEffect(() => {
    if (open && loadExistingIdentities && !identitiesLoaded) {
      loadExistingIdentities()
        .then((groups) => {
          setExistingIdentities(groups);
          setIdentitiesLoaded(true);
        })
        .catch(() => setIdentitiesLoaded(true));
    }
  }, [open, loadExistingIdentities, identitiesLoaded]);

  const handleChange = (field: string) => (event: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        alert("Please select an image file");
        return;
      }
      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert("File size must be less than 10MB");
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleAssetSelect = (asset: Asset) => {
    setSelectedAsset(asset);
    setSelectedFile(null); // Clear file if asset is selected
    setAssetLibraryOpen(false);
  };

  const handleSubmit = async () => {
    // V3: reference images are ONLY allowed when an avatarGroupId is selected.
    // Backend rejects this combination with 400 — guard client-side as well.
    const referenceImages =
      selectedAvatarGroupId && referenceImageUrls.length > 0
        ? referenceImageUrls.map((url) => ({ type: "url" as const, url }))
        : undefined;
    const v3Extras = {
      ...(selectedAvatarGroupId && { avatarGroupId: selectedAvatarGroupId }),
      ...(referenceImages && { referenceImages }),
    };

    if (activeTab === 0) {
      // Generate avatar (V3 prompt-to-avatar)
      await onSubmit({ ...formData, ...v3Extras }, "generate");
    } else if (activeTab === 2 && selectedVideoFile) {
      // V3 digital twin from video
      await onSubmit({ ...formData, ...v3Extras }, "video", undefined, undefined, selectedVideoFile);
    } else if (activeTab === 1) {
      // Upload avatar - either from file or from library
      if (selectedAsset) {
        // Asset library — still uses the V2 URL endpoint (no V3 URL endpoint exists)
        await onSubmit({ ...formData, ...v3Extras }, "library", undefined, selectedAsset.url);
      } else if (selectedFile) {
        // V3 image upload
        await onSubmit({ ...formData, ...v3Extras }, "upload", selectedFile);
      } else {
        alert("Please select an image file or choose from library");
        return;
      }
    }

    // Reset form after successful submission
    setFormData({
      name: "",
      age: "Unspecified",
      gender: "Unspecified",
      ethnicity: "Unspecified",
      orientation: "square",
      pose: "half_body",
      style: "Unspecified",
      appearance: "",
    });
    setSelectedFile(null);
    setSelectedVideoFile(null);
    setSelectedAsset(null);
    setSelectedAvatarGroupId("");
    setReferenceImageUrls([]);
  };

  const canSubmit =
    formData.name.trim() !== "" &&
    !isLoading &&
    (activeTab === 0 ||
      (activeTab === 1 && (selectedFile || selectedAsset)) ||
      (activeTab === 2 && selectedVideoFile));

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md">
      <DialogTitle>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Create Custom Photo Avatar</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent>
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
            <Tab
              label="Generate Avatar"
              sx={{
                color: "text.primary",
                "&.Mui-selected": { color: "primary.main" },
              }}
            />
            <Tab
              label="Upload Avatar"
              sx={{
                color: "text.primary",
                "&.Mui-selected": { color: "primary.main" },
              }}
            />
            <Tab
              label="Digital Twin (Video)"
              sx={{
                color: "text.primary",
                "&.Mui-selected": { color: "primary.main" },
              }}
            />
          </Tabs>
        </Box>

        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              className={globalClasses.textFieldLabel}
              fullWidth
              label="Avatar Name *"
              value={formData.name}
              onChange={handleChange("name")}
              placeholder="Enter a unique name for your avatar"
            />
          </Grid>

          {/* V3: Existing-identity picker (shown on all tabs) */}
          {loadExistingIdentities && (
            <Grid item xs={12}>
              <FormControl fullWidth>
                <StyledInputLabel shrink>Attach to existing identity (optional)</StyledInputLabel>
                <Select
                  value={selectedAvatarGroupId}
                  onChange={(e) => setSelectedAvatarGroupId(e.target.value as string)}
                  label="Attach to existing identity (optional)"
                  displayEmpty
                  notched
                >
                  <MenuItem value="">
                    <em>Create a new identity</em>
                  </MenuItem>
                  {existingIdentities.map((g) => (
                    <MenuItem key={g.id} value={g.id}>
                      {g.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" sx={{ mt: 0.5, display: "block", color: "grey.700" }}>
                Attaching to an existing identity adds this look to that avatar's library.
              </Typography>
            </Grid>
          )}

          {/* V3: Reference images uploader — only allowed when an identity is selected */}
          {activeTab === 0 && selectedAvatarGroupId && (
            <Grid item xs={12}>
              <TextField
                className={globalClasses.textFieldLabel}
                fullWidth
                label="Reference image URLs (comma-separated, max 3)"
                value={referenceImageUrls.join(", ")}
                onChange={(e) =>
                  setReferenceImageUrls(
                    e.target.value
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean)
                      .slice(0, 3)
                  )
                }
                placeholder="https://..., https://..., https://..."
                helperText="Reference images are only allowed when attaching to an existing identity."
                FormHelperTextProps={{ sx: { color: "grey.700" } }}
              />
            </Grid>
          )}

          {/* Upload Tab Content */}
          {activeTab === 1 && (
            <>
              {/* Gender selection for upload tab - ONLY this additional field */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <StyledInputLabel>Gender</StyledInputLabel>
                  <Select value={formData.gender} onChange={handleChange("gender")} label="Gender">
                    <MenuItem value="female">Female</MenuItem>
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="Unspecified">Unspecified</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                {/* Show preview if file or asset is selected */}
                {(selectedFile || selectedAsset) && (
                  <Box sx={{ mb: 2, textAlign: "center" }}>
                    <img
                      src={selectedAsset ? selectedAsset.url : selectedFile ? URL.createObjectURL(selectedFile) : ""}
                      alt="Preview"
                      style={{
                        maxWidth: "200px",
                        maxHeight: "200px",
                        borderRadius: "8px",
                        objectFit: "cover",
                      }}
                    />
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      {selectedAsset ? selectedAsset.name : selectedFile?.name}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: "flex", gap: 1, flexDirection: { xs: "column", sm: "row" } }}>
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    fullWidth
                    sx={{ py: 2 }}
                  >
                    {selectedFile ? "Change File" : "Upload Avatar Image"}
                    <VisuallyHiddenInput
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        handleFileChange(e);
                        setSelectedAsset(null); // Clear asset when file is selected
                      }}
                    />
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CollectionsIcon />}
                    fullWidth
                    onClick={() => setAssetLibraryOpen(true)}
                    sx={{
                      py: 2,
                      borderColor: THEME_COLORS.yellow,
                      color: THEME_COLORS.darkYellow,
                      "&:hover": {
                        borderColor: THEME_COLORS.darkYellow,
                        backgroundColor: "rgba(255, 191, 0, 0.1)",
                      },
                    }}
                  >
                    From Library
                  </Button>
                </Box>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ mt: 1, display: "block" }}
                >
                  Supported formats: JPG, PNG, WebP. Max size: 10MB
                </Typography>
              </Grid>
            </>
          )}

          {/* Generate Tab Content - All original fields */}
          {activeTab === 0 && (
            <>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <StyledInputLabel>Age</StyledInputLabel>
                  <Select value={formData.age} onChange={handleChange("age")} label="Age">
                    <MenuItem value="Young Adult">Young Adult</MenuItem>
                    <MenuItem value="Early Middle Age">Early Middle Age</MenuItem>
                    <MenuItem value="Late Middle Age">Late Middle Age</MenuItem>
                    <MenuItem value="Senior">Senior</MenuItem>
                    <MenuItem value="Unspecified">Unspecified</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <StyledInputLabel>Gender</StyledInputLabel>
                  <Select
                    value={formData.gender}
                    onChange={handleChange("gender")}
                    label="Gender"
                  >
                    <MenuItem value="female">Female</MenuItem>
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="Unspecified">Unspecified</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <StyledInputLabel>Ethnicity</StyledInputLabel>
                  <Select
                    value={formData.ethnicity}
                    onChange={handleChange("ethnicity")}
                    label="Ethnicity"
                  >
                    <MenuItem value="White">White</MenuItem>
                    <MenuItem value="Black">Black</MenuItem>
                    <MenuItem value="Asian American">Asian American</MenuItem>
                    <MenuItem value="East Asian">East Asian</MenuItem>
                    <MenuItem value="South East Asian">South East Asian</MenuItem>
                    <MenuItem value="South Asian">South Asian</MenuItem>
                    <MenuItem value="Middle Eastern">Middle Eastern</MenuItem>
                    <MenuItem value="Pacific">Pacific</MenuItem>
                    <MenuItem value="Hispanic">Hispanic</MenuItem>
                    <MenuItem value="Unspecified">Unspecified</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <StyledInputLabel>Orientation</StyledInputLabel>
                  <Select
                    value={formData.orientation}
                    onChange={handleChange("orientation")}
                    label="Orientation"
                  >
                    <MenuItem value="square">Square</MenuItem>
                    <MenuItem value="horizontal">Horizontal</MenuItem>
                    <MenuItem value="vertical">Vertical</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <StyledInputLabel>Pose</StyledInputLabel>
                  <Select
                    value={formData.pose}
                    onChange={handleChange("pose")}
                    label="Pose"
                  >
                    <MenuItem value="half_body">Half Body</MenuItem>
                    <MenuItem value="close_up">Close Up</MenuItem>
                    <MenuItem value="full_body">Full Body</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <StyledInputLabel>Style</StyledInputLabel>
                  <Select value={formData.style} onChange={handleChange("style")} label="Style">
                    <MenuItem value="Realistic">Realistic</MenuItem>
                    <MenuItem value="Pixar">Pixar</MenuItem>
                    <MenuItem value="Cinematic">Cinematic</MenuItem>
                    <MenuItem value="Vintage">Vintage</MenuItem>
                    <MenuItem value="Noir">Noir</MenuItem>
                    <MenuItem value="Cyberpunk">Cyberpunk</MenuItem>
                    <MenuItem value="Unspecified">Unspecified</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={1}>
                  <TextField
                    className={globalClasses.textFieldLabel}
                    fullWidth
                    multiline
                    rows={3}
                    label={
                      <Box display="flex" alignItems="center" gap={0.5}>
                        Appearance Description
                        <Tooltip
                          title={
                            <>
                              Not sure what to type? Describe:
                              <br />
                              • Clothing & style: "business suit", "casual hoodie"
                              <br />
                              • Pose & gestures: "holding a phone", "arms crossed"
                              <br />
                              • Setting & ambience: "office background", "warm lighting"
                              <br />
                              • Framing: "frontal camera angle", "selfie-style"
                            </>
                          }
                          placement="top"
                          arrow
                        >
                          <IconButton size="small">
                            <InfoOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                    value={formData.appearance}
                    onChange={handleChange("appearance")}
                    placeholder="e.g., 'Professional attire, warm smile, soft office lighting, frontal selfie-style framing' or 'Casual hoodie, relaxed expression, cozy room ambience'"
                    helperText="Optional: Provide detailed description of how you want the avatar to look"
                  />
                </Box>
              </Grid>
            </>
          )}

          {/* Digital Twin Tab Content (V3) */}
          {activeTab === 2 && (
            <>
              <Grid item xs={12}>
                {selectedVideoFile && (
                  <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                    Selected: {selectedVideoFile.name} ({Math.round(selectedVideoFile.size / 1024 / 1024)} MB)
                  </Typography>
                )}
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  fullWidth
                  sx={{ py: 2 }}
                >
                  {selectedVideoFile ? "Change Video" : "Upload Avatar Video"}
                  <VisuallyHiddenInput
                    type="file"
                    accept="video/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) setSelectedVideoFile(file);
                    }}
                  />
                </Button>
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: "block" }}>
                  Supported formats: MP4, MOV, etc. Max size: 200MB. Training is asynchronous.
                </Typography>
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ padding: "20px" }}>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canSubmit}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isLoading
            ? activeTab === 0
              ? "Generating..."
              : activeTab === 2
                ? "Uploading Video..."
                : "Uploading..."
            : activeTab === 0
              ? "Generate Avatar"
              : activeTab === 2
                ? "Create Digital Twin"
                : "Upload Avatar"}
        </Button>
      </DialogActions>

      {/* Asset Library Modal */}
      <AssetLibraryModal
        open={assetLibraryOpen}
        onClose={() => setAssetLibraryOpen(false)}
        mode="select"
        onSelect={handleAssetSelect}
        filterType="image"
        title="Select Image from Library"
      />
    </StyledDialog>
  );
};

export default AvatarCreationModal;
